import useSWR from "swr";
import {
  scanSubitemDiscrepancies,
  scanSubitemDiscrepancyParent,
} from "../../utils/requests/subitemDiscrepancyRequests";
import {
  SubitemScanParent,
  SubitemScanResponse,
} from "../../components/Resynchronization/__fixtures__/discrepancyFixtures";

const useSubitemDiscrepancy = (
  currentBoard: number | null,
  token: string | null,
  parentSyncIds: string[],
) => {
  // Sorted and joined, not the raw array. SWR keys on the string, and the API
  // hands the same page of sync ids back in a non-guaranteed order — an
  // unsorted key would make every re-render a cache MISS and re-scan.
  const key =
    currentBoard && token && parentSyncIds.length > 0
      ? `subitemDiscrepancy:${currentBoard}:${[...parentSyncIds].sort().join(",")}`
      : null;

  const { isLoading, error, data, mutate, isValidating } = useSWR<SubitemScanResponse>(
    key,
    () => scanSubitemDiscrepancies(token!, String(currentBoard), parentSyncIds),
    { revalidateOnFocus: false, dedupingInterval: 5000 },
  );

  // Scans exactly the one parent the bulk scan deferred (or couldn't verify), and merges its
  // result into the already-cached page via a local cache write — `revalidate: false` — rather
  // than a bare `mutate()`. A parent is deferred precisely because scanning it up front is too
  // expensive; re-running the bulk scan for every OTHER parent on this page just to pick up
  // this one would be exactly the cost this endpoint exists to avoid.
  //
  // `/scan-parent` returns the SAME `{ parents: [...] }` envelope the bulk scan does, never a
  // bare parent: the engine's `scanParent` is literally `scan({ parentSyncIds: [one] })`
  // (simb-integration-ms/src/api/subitem-discrepancy/subitem-discrepancy.service.ts:336-343),
  // and simb-backend proxies it verbatim. Treating the envelope as a parent left
  // `scanned.parentSyncId` undefined, so the merge below matched nothing, the parent stayed
  // `deferred` forever, and every click re-paid for a full Monday scan that changed nothing.
  //
  // The lookup is keyed off the id that was REQUESTED, not off anything in the response, so a
  // further shape change cannot silently degrade into a no-op again — it throws instead.
  const scanParent = async (parentSyncId: string): Promise<SubitemScanParent> => {
    const response: SubitemScanResponse = await scanSubitemDiscrepancyParent(
      token!,
      String(currentBoard),
      parentSyncId,
    );
    const scanned = response?.parents?.find((parent) => parent.parentSyncId === parentSyncId);
    if (!scanned) {
      throw new Error(`scan-parent returned no result for ${parentSyncId}`);
    }
    mutate(
      (current) => {
        // `undefined` is NOT a no-op for an SWR updater — SWR commits whatever the updater
        // returns as the new cache value, so returning `current && {...}` on an empty cache
        // would blank the page rather than leave it alone. Returning `current` itself is the
        // actual no-op, for the case where there is nothing cached yet to merge into.
        if (!current) return current;
        return {
          ...current,
          parents: current.parents.map((existing) =>
            existing.parentSyncId === parentSyncId ? scanned : existing,
          ),
        };
      },
      { revalidate: false },
    );
    return scanned;
  };

  return { isLoading, error, parents: data?.parents ?? [], mutate, isValidating, scanParent };
};

export default useSubitemDiscrepancy;
